import { Button, debounce, MenuItem, Stack, Typography } from '@mui/material';
import DrawerWrapper from '@components/drawer/drawer-wrapper';
import ContainerTitle from '@components/ui/container-title';
import { useGetCLAccountsBookingsQuery } from '@api/front-office/cl-accounts.api.rtk';
import ServerError from '@components/animation/server-error';
import DataTable from '@components/table/datatable';
import useMenu from '@hooks/useMenu';
import { CLBookingDetailsList } from '@modules/front-office/interface/fo-cl-accounts-bookings-interface';
import { Box, Checkbox, Divider, FormControlLabel } from '@mui/material';
import {
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import useDisclosure from '@hooks/useDisclosure';
import { GuestFolioModal } from '../room-posting/voucher/fo-room-posting-guest-folio';

const CustomToolbar = ({
  showIsClosedCLTrans,
  onShowIsClosedCLTransChange,
}: {
  showIsClosedCLTrans: boolean;
  onShowIsClosedCLTransChange: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <Divider
        orientation='vertical'
        sx={{
          height: '36px',
          mr: '8px',
          display: { xs: 'none', sm: 'inline-block' },
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={showIsClosedCLTrans}
            onChange={(event) =>
              onShowIsClosedCLTransChange(event.target.checked)
            }
          />
        }
        label='Show Closed Bookings'
      />
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter
        sx={{
          ml: { xs: '0', sm: 'auto' },
          width: {
            xs: '100%!important',
            lg: '200px!important',
            xl: '220px!important',
          },
        }}
      />
    </GridToolbarContainer>
  );
};

const CLAccBookingsColumn: GridColDef[] = [
  {
    field: 'bookingReferenceID',
    headerName: 'Reference ID',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'cityLedgerNumber',
    headerName: 'CL No',
    maxWidth: 60,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'folioNumber',
    headerName: 'Folio No',
    maxWidth: 75,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'marketSegment',
    headerName: 'Market Segment',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'businessSource',
    headerName: 'Source',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'guestName',
    headerName: 'Guest Name',
    minWidth: 100,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'room',
    headerName: 'Room',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
  {
    field: 'endingBalance',
    headerName: 'Original Balance',
    minWidth: 50,
    flex: 1,
    hideable: false,
    sortable: false,
  },
];

const VisibleColumns = CLAccBookingsColumn.filter(
  (column) => !['bookingReferenceID'].includes(column.field),
);

const calculateEndingBalanceTotal = (data: CLBookingDetailsList[]) => {
  return data
    .reduce(
      (acc, curr) =>
        acc + parseFloat(curr.endingBalance?.replace(/,/g, '') || '0'),
      0,
    )
    .toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
};

const FOCityLedgerAccountsBookings = ({
  isOpen,
  onClose,
  accountID,
}: {
  isOpen?: boolean;
  onClose: () => void;
  accountID?: string | null | undefined;
}) => {
  const {
    handleContextMenu,
    MenuWrapper,
    selectedData,
    manualSetSelectedData,
    handleTouchEnd,
    handleTouchStart,
    handleClose,
  } = useMenu();

  const [clBookingsData, setclBookingsData] = useState<CLBookingDetailsList[]>(
    [] as CLBookingDetailsList[],
  );

  const [endingBalanceTotalState, setEndingBalanceTotalState] =
    useState('0.00');

  const [isShowClosedCLTrans, setIsShowClosedCLTrans] = useState(false);

  const {
    data: CLAccountBookingList,
    isLoading,
    isFetching,
    isError,
  } = useGetCLAccountsBookingsQuery(
    { accountID, showIsClosed: isShowClosedCLTrans ? 1 : 0 },
    {
      skip: !accountID,
    },
  );

  useEffect(() => {
    if (!CLAccountBookingList?.data?.cityLedgerList?.length) return;
    const CLBookingsDetailsList: CLBookingDetailsList[] =
      CLAccountBookingList.data.cityLedgerList.flatMap((account) =>
        account.cityLedgers.flatMap((ledger) => ledger.cityLedger),
      );
    setclBookingsData(CLBookingsDetailsList);
  }, [CLAccountBookingList]);

  useEffect(() => {
    setEndingBalanceTotalState(calculateEndingBalanceTotal(clBookingsData));
  }, [clBookingsData]);

  const handleFilterModelChange = debounce((value) => {
    const data =
      CLAccountBookingList?.data?.cityLedgerList?.[0]?.cityLedgers[0]
        .cityLedger;

    const valueFilter = value?.quickFilterValues?.[0]?.toLowerCase();

    if (valueFilter) {
      const filteredEntries = data?.filter((entry) =>
        Object.values(entry).some((fieldValue) =>
          String(fieldValue).toLowerCase().includes(valueFilter),
        ),
      );
      setEndingBalanceTotalState(
        calculateEndingBalanceTotal(filteredEntries || []),
      );
    } else {
      setEndingBalanceTotalState(calculateEndingBalanceTotal(clBookingsData));
    }
  }, 1000);

  const accountName =
    CLAccountBookingList?.data?.cityLedgerList[0]?.cityLedgers[0]
      ?.accountName || '';
  const totalBalance =
    CLAccountBookingList?.data?.cityLedgerList[0]?.cityLedgers[0]
      ?.totalBalance || 0;

  const printGuestFolioDisclosure = useDisclosure();

  const handleClosed = () => {
    setIsShowClosedCLTrans(false);
    setEndingBalanceTotalState(calculateEndingBalanceTotal(clBookingsData));
    onClose();
  };

  return (
    <DrawerWrapper open={isOpen} onClose={onClose} stickyCloseIcon>
      <Stack
        flex={1}
        sx={{
          width: { xs: '100%', lg: '981px' },
          overflow: { sx: 'auto', md: 'auto' },
        }}
      >
        {/* HEADER */}
        <Stack
          display={{ xs: 'none', md: 'flex' }}
          direction={{ xs: 'column', md: 'row' }}
          sx={{
            p: '20px',
            borderBottom: '1px solid #EEE',
            position: 'sticky',
            top: 0,
            bgcolor: 'white',
            zIndex: 21,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{
              pl: '5px',
            }}
          >
            <ContainerTitle
              title={accountName}
              func={() => {}}
              titleStyle={{ fontSize: '20px!important' }}
            />
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              gap={{ xs: 2, md: 3 }}
              sx={{
                marginLeft: '30px',
                alignItems: 'center',
                justifyContent: 'space-evenly',
              }}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-evenly',
                  pt: { xs: '2px', md: '2px' },
                }}
              >
                <Typography
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Search Result Total:
                  <Typography
                    component='span'
                    sx={{
                      fontWeight: 550,
                      fontSize: '20px',
                      color: '#2B3493',
                      marginLeft: '7px',
                    }}
                  >
                    {endingBalanceTotalState}
                  </Typography>
                </Typography>
              </Stack>
              <Stack
                sx={{
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pt: { xs: '2px', md: '2px' },
                }}
              >
                <Typography
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Total Outstanding City Ledger:
                  <Typography
                    component='span'
                    sx={{
                      fontWeight: 550,
                      fontSize: '20px',
                      color: '#2B3493',
                      marginLeft: '7px',
                    }}
                  >
                    {totalBalance}
                  </Typography>
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        {/* MAIN BODY */}
        {isError ? (
          <ServerError />
        ) : (
          <DataTable
            onFilterModelChange={handleFilterModelChange}
            getRowId={(row) => row.bookingReferenceID}
            columns={VisibleColumns}
            rows={isError ? [] : clBookingsData || []}
            checkboxSelection={false}
            loading={isLoading || isFetching}
            pageSize={100}
            autoPageSize={false}
            onRowDoubleClick={(row) => {
              manualSetSelectedData(row.id.toString());
            }}
            tableStyle={{
              ".MuiButtonBase-root[aria-label='Export']": {
                display: 'none!important',
              },
            }}
            slotProps={{
              row: {
                onContextMenu: handleContextMenu,
                onTouchStart: handleTouchStart,
                onTouchEnd: handleTouchEnd,
                style: { cursor: 'context-menu' },
              },
              toolbar: {
                showIsClosedCLTrans: isShowClosedCLTrans,
                onShowIsClosedCLTransChange: setIsShowClosedCLTrans,
              },
            }}
            slots={{
              toolbar: CustomToolbar,
            }}
            responsiveHeight
            sortBy='cityLedgerNumber'
          />
        )}
        <MenuWrapper>
          <MenuItem
            onClick={() => {
              printGuestFolioDisclosure.onOpen();
              handleClose();
            }}
          >
            Print Guest Folio
          </MenuItem>
        </MenuWrapper>
      </Stack>
      {/* FOOTER */}
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='flex-end'
        paddingRight={5}
        paddingBottom={2}
      >
        <Button variant='outlined' onClick={handleClosed}>
          Close
        </Button>
      </Stack>
      {useMemo(
        () => (
          <GuestFolioModal
            {...{
              bookingReferenceId: selectedData!,
              isOpen: printGuestFolioDisclosure.isOpen,
              handleModalClose: printGuestFolioDisclosure.onClose,
            }}
          />
        ),
        [printGuestFolioDisclosure],
      )}
    </DrawerWrapper>
  );
};

export default FOCityLedgerAccountsBookings;
